import React, { useState, useRef, useEffect } from 'react';
import { Input, Button, Select, Tooltip, Typography, message, notification, Popover, Tag } from 'antd';
import {
  UploadOutlined,
  SendOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FormOutlined,
  CloseOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  FilePptOutlined,
  InboxOutlined,
  LoadingOutlined,
  CheckCircleFilled,
  ExclamationCircleFilled,
  HistoryOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import { uploadCollabFile } from '../../services/teamCollabApi';
import api from '../../services/api';
import VoiceInputButton from '../../components/VoiceInputButton';

const { TextArea } = Input;
const { Text } = Typography;

interface UserComp {
  id: string; uuid?: string; slug?: string; name: string;
  description?: string; version?: string; type?: string;
}

interface InputBarProps {
  onSubmit: (command: string, resultType: string, kbDepth: number, fileContent?: string, fileName?: string, taskType?: string, useKb?: boolean, isPrivate?: boolean, enableThinking?: boolean, modelId?: string) => void;
  loading: boolean;
  draftCommand?: string;
  draftFileName?: string;
  draftFileContent?: string;
  onDraftConsumed?: () => void;
  onOpenHistory?: () => void;
}

interface AttachedFile {
  id: string;
  name: string;
  content: string;
  uploading: boolean;
  error?: string;
  charCount?: number;
  tokenEstimate?: number; // 识别/解析出的文本的粗略 token 数，展示在附件区
  progressMsg?: string;   // 上传/识别中的实时进度文案（如 "逐页识别 30/71 页"）
}

const MAX_FILES = 5;
const ACCEPTED_EXTENSIONS = ['txt', 'md', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'pdf', 'png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp'];
const ACCEPT_ATTR = '.txt,.md,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf,.png,.jpg,.jpeg,.webp,.gif,.bmp';

type KbMode = 'off' | 'normal' | 'strong' | 'max';
const kbModeOptions: { value: KbMode; label: string; depth: number; useKb: boolean }[] = [
  { value: 'off', label: '知识库（关闭）', depth: 10, useKb: false },
  { value: 'normal', label: '知识库（普通）', depth: 10, useKb: true },
  { value: 'strong', label: '知识库（强匹配）', depth: 20, useKb: true },
  { value: 'max', label: '知识库（最强）', depth: 50, useKb: true },
];

const fileTypeIcon = (name: string) => {
  const ext = name.split('.').pop()?.toLowerCase();
  if (ext === 'doc' || ext === 'docx') return <FileWordOutlined className="tc-jm-file-icon word" />;
  if (ext === 'xls' || ext === 'xlsx') return <FileExcelOutlined className="tc-jm-file-icon excel" />;
  if (ext === 'pdf') return <FilePdfOutlined className="tc-jm-file-icon pdf" />;
  if (ext === 'ppt' || ext === 'pptx') return <FilePptOutlined className="tc-jm-file-icon ppt" />;
  if (['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp'].includes(ext || '')) return <PictureOutlined className="tc-jm-file-icon image" />;
  return <FileTextOutlined className="tc-jm-file-icon text" />;
};

const outputOptions = [
  { value: 'text', label: '文本', icon: <FormOutlined /> },
  { value: 'word', label: 'Word', icon: <FileWordOutlined /> },
  { value: 'excel', label: 'Excel', icon: <FileExcelOutlined /> },
  { value: 'pdf', label: 'PDF', icon: <FilePdfOutlined /> },
];

const quoteOptions = [
  { value: '', label: '不报价' },
  { value: 'quote_dev', label: '研发报价' },
  { value: 'quote_impl', label: '实施报价' },
];

const InputBar: React.FC<InputBarProps> = ({ onSubmit, loading, draftCommand, draftFileName, draftFileContent, onDraftConsumed, onOpenHistory }) => {
  const [command, setCommand] = useState('');
  const [resultType, setResultType] = useState('text');
  const [taskType, setTaskType] = useState<string>('');
  const [kbMode, setKbMode] = useState<KbMode>('max');
  const [enableThinking, setEnableThinking] = useState(false);
  const [modelId, setModelId] = useState<string | undefined>(undefined);
  const [models, setModels] = useState<{ id: string; model: string; provider: string; apiUrl: string }[]>([]);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // 组件库
  const [userComps, setUserComps] = useState<UserComp[]>([]);
  const [compPopOpen, setCompPopOpen] = useState(false);

  const currentKb = kbModeOptions.find(item => item.value === kbMode) || kbModeOptions[0];
  const usableFiles = attachedFiles.filter(f => !f.uploading && !f.error && f.content);
  const uploadingCount = attachedFiles.filter(f => f.uploading).length;
  const hasPendingUpload = uploadingCount > 0;

  useEffect(() => {
    if (draftCommand) {
      setCommand(draftCommand);
      if (draftFileName) {
        setAttachedFiles([{ id: `${Date.now()}_draft`, name: draftFileName, content: draftFileContent || '', uploading: false, charCount: (draftFileContent || '').length }]);
      }
      onDraftConsumed?.();
    }
  }, [draftCommand, draftFileName, draftFileContent]);

  useEffect(() => {
    api.get('/llm/models-with-provider').then(r => {
      setModels(r.data || []);
      if (r.data?.length > 0 && !modelId) setModelId(r.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/tech-components/user').then(r => setUserComps(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      if (command.trim()) localStorage.setItem('collab_draft_command', command);
    }, 5000);
    return () => clearInterval(timer);
  }, [command]);

  useEffect(() => {
    const saved = localStorage.getItem('collab_draft_command');
    if (saved && !draftCommand) setCommand(saved);
  }, []);

  const buildCombinedFileContent = () => {
    if (!usableFiles.length) return undefined;
    return usableFiles.map((file, index) => (
      `【附件${index + 1}：${file.name}】\n${file.content}`
    )).join('\n\n---\n\n');
  };

  const handleSubmit = () => {
    const trimmed = command.trim();
    const fileContent = buildCombinedFileContent();
    if (!trimmed && !fileContent) return;
    if (hasPendingUpload) {
      message.warning('还有文件正在解析，请稍等完成后再发送');
      return;
    }
    onSubmit(
      trimmed || '请分析上传的文件内容',
      resultType,
      currentKb.depth,
      fileContent,
      usableFiles.map(file => file.name).join('、') || undefined,
      taskType || undefined,
      currentKb.useKb,
      true,
      enableThinking,
      modelId,
    );
    setCommand('');
    setAttachedFiles([]);
    setTaskType('');
    setResultType('text');
    localStorage.removeItem('collab_draft_command');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const validateFile = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !ACCEPTED_EXTENSIONS.includes(ext)) {
      notification.warning({ message: '格式不支持', description: `不支持 .${ext || '未知'} 格式；支持 Word、Excel、PowerPoint、PDF、文本和图片`, duration: 5, placement: 'topRight' });
      return false;
    }
    if (file.size > 100 * 1024 * 1024) {
      notification.warning({ message: '文件过大', description: '单个文件大小不能超过 100MB', duration: 5, placement: 'topRight' });
      return false;
    }
    return true;
  };

  const handleFilesSelect = async (fileList: FileList | File[]) => {
    const incoming = Array.from(fileList);
    if (!incoming.length) return;
    const availableSlots = MAX_FILES - attachedFiles.length;
    if (availableSlots <= 0) {
      notification.warning({ message: '附件已满', description: `最多上传 ${MAX_FILES} 个文件`, duration: 3, placement: 'topRight' });
      return;
    }
    const accepted = incoming.filter(validateFile).slice(0, availableSlots);
    if (incoming.length > availableSlots) {
      notification.warning({ message: '已限制数量', description: `最多上传 ${MAX_FILES} 个文件，本次只加入前 ${availableSlots} 个`, duration: 3, placement: 'topRight' });
    }
    for (const file of accepted) {
      const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      setAttachedFiles(prev => [...prev, { id, name: file.name, content: '', uploading: true }]);
      try {
        const result = await uploadCollabFile(file, (msg) => {
          setAttachedFiles(prev => prev.map(item => item.id === id ? { ...item, progressMsg: msg } : item));
        });
        if (result.success && result.content) {
          setAttachedFiles(prev => prev.map(item => item.id === id ? { ...item, name: result.fileName || file.name, content: result.content || '', uploading: false, charCount: result.charCount || result.content?.length || 0, tokenEstimate: result.tokenEstimate } : item));
          message.success({ content: `✅ ${file.name} 解析完成`, duration: 2, style: { marginTop: '60vh' } });
        } else {
          const errMsg = result.error || '文件解析失败';
          setAttachedFiles(prev => prev.map(item => item.id === id ? { ...item, uploading: false, error: errMsg } : item));
          notification.error({ message: '文件上传失败', description: errMsg, duration: 5, placement: 'topRight' });
        }
      } catch (e: any) {
        let errMsg = '网络错误，请检查连接';
        if (e.response?.data?.message) errMsg = Array.isArray(e.response.data.message) ? e.response.data.message.join('; ') : e.response.data.message;
        else if (e.response?.data?.error) errMsg = e.response.data.error;
        else if (e.message) errMsg = e.message;
        setAttachedFiles(prev => prev.map(item => item.id === id ? { ...item, uploading: false, error: errMsg } : item));
        notification.error({ message: '文件上传失败', description: errMsg, duration: 5, placement: 'topRight' });
      }
    }
  };

  const removeFile = (id: string) => setAttachedFiles(prev => prev.filter(file => file.id !== id));

  const insertAtCursor = (text: string) => {
    const el = textareaRef.current?.resizableTextArea?.textArea;
    if (!el) { setCommand(prev => prev ? `${prev} ${text}` : text); return; }
    const start = el.selectionStart ?? command.length;
    const end = el.selectionEnd ?? start;
    const before = command.slice(0, start);
    const after = command.slice(end);
    const spacer = before && !before.endsWith(' ') ? ' ' : '';
    const newCmd = `${before}${spacer}${text}${after}`;
    setCommand(newCmd);
    // 恢复光标位置在插入文本之后
    setTimeout(() => {
      const pos = start + spacer.length + text.length;
      el.setSelectionRange(pos, pos);
      el.focus();
    }, 0);
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    await handleFilesSelect(e.dataTransfer.files);
  };
  // 粘贴上传：在输入框里 Ctrl+V 粘贴“从资源管理器复制的文件”或“截图”，直接作为附件引用。
  const handlePaste = async (e: React.ClipboardEvent) => {
    const dt = e.clipboardData;
    if (!dt) {
      notification.warning({ message: '未读取到剪贴板内容', description: '请改用上传按钮或把文件拖入输入区', duration: 8, placement: 'topRight' });
      return;
    }
    const files: File[] = [];
    if (dt.files && dt.files.length) {
      for (const f of Array.from(dt.files)) files.push(f);
    } else if (dt.items) {
      for (const it of Array.from(dt.items)) {
        if (it.kind === 'file') {
          const f = it.getAsFile();
          if (f) {
            // 截图等无文件名的，按 MIME 补一个名字，便于走扩展名校验
            const named = f.name
              ? f
              : new File([f], `粘贴图片-${Date.now()}.${(f.type.split('/')[1] || 'png')}`, { type: f.type });
            files.push(named);
          }
        }
      }
    }
    if (files.length) {
      e.preventDefault(); // 有文件才拦截：避免把图片/文件名当普通文本粘进输入框
      await handleFilesSelect(files);
      return;
    }
    const plainText = dt.getData('text/plain').trim();
    const reportsFile = Array.from(dt.types || []).some(type => type.toLowerCase() === 'files')
      || Array.from(dt.items || []).some(item => item.kind === 'file');
    const looksLikeFile = plainText.length < 2048
      && /(?:^|[\\/\s])[^\\/\r\n]+\.(?:pptx?|pdf|docx?|xlsx?|txt|md|png|jpe?g|webp|gif|bmp)$/i.test(plainText);
    if (reportsFile || looksLikeFile) {
      e.preventDefault();
      notification.warning({
        message: '没有取得文件内容',
        description: '浏览器只提供了文件名或路径。请直接拖入文件，或点击上传按钮选择。',
        duration: 8, placement: 'topRight',
      });
    }
    // 真正的纯文本粘贴不拦截
  };

  return (
    <div className="df-wr-composer">
      <section className="df-wr-composer-card" onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
        {dragOver && (
          <div className="tc-jm-drop-mask">
            <InboxOutlined />
            <span>释放文件以上传解析，最多 5 个</span>
          </div>
        )}

        <header className="tc-right-composer-header">
          <div className="tc-right-composer-title-block">
            <span className="tc-right-composer-eyebrow">WORKROOM COMMAND</span>
            <strong>智能工坊指令台</strong>
            <span>模型、知识库、组件、历史与附件全部在这里配置</span>
          </div>
          <div className="tc-right-composer-status">
            <CheckCircleFilled />
            <span>{currentKb.useKb ? `知识库 ${currentKb.depth}` : '知识库关闭'}</span>
          </div>
        </header>

        <section className="tc-right-toolbox" aria-label="智能工坊操作项">
          <div className="tc-right-tool-field tc-right-tool-output"><span className="tc-right-tool-label">输出类型</span>
            <Select size="small" value={resultType} className="tc-jm-select tc-jm-output-select" popupMatchSelectWidth={false} onChange={(value) => { setResultType(value); if (value !== 'excel') setTaskType(''); }} options={outputOptions.map(item => ({ value: item.value, label: <span className="tc-jm-option-label">{item.icon}{item.label}</span> }))} />
          </div>
          <div className="tc-right-tool-field tc-right-tool-quote"><span className="tc-right-tool-label">报价类型</span>
            <Select size="small" value={taskType} className="tc-jm-select tc-jm-quote-select" popupMatchSelectWidth={false} onChange={(value) => { setTaskType(value); if (value) setResultType('excel'); }} options={quoteOptions} />
          </div>
          <div className="tc-right-tool-field tc-right-tool-kb"><span className="tc-right-tool-label">知识库引用</span>
            <Select size="small" value={kbMode} className={`tc-jm-select tc-jm-kb-select ${kbMode === 'off' ? 'is-off' : 'is-active'}`} popupMatchSelectWidth={false} onChange={setKbMode} options={kbModeOptions.map(item => ({ value: item.value, label: item.label }))} />
          </div>
          <div className="tc-right-tool-field tc-right-tool-model"><span className="tc-right-tool-label">模型选择</span>
            <Select size="small" value={modelId} onChange={setModelId} className="tc-jm-select tc-jm-model-select" popupMatchSelectWidth={false} options={models.map(m => ({ value: m.id, label: `${m.provider ? m.provider + ' / ' : ''}${m.model}${m.apiUrl ? ' (' + m.apiUrl + ')' : ''}` }))} placeholder="模型" />
          </div>
          <Button size="small" className={`tc-jm-button tc-right-tool-button tc-right-thinking-button ${enableThinking ? 'is-thinking-on' : 'is-muted-off'}`} onClick={() => setEnableThinking(!enableThinking)}>{enableThinking ? '当前：思考已开启' : '当前：思考已关闭'}</Button>
          <Popover open={compPopOpen} onOpenChange={v => { setCompPopOpen(v); if (v && userComps.length === 0) { api.get('/tech-components/user').then(r => setUserComps(r.data || [])).catch(() => {}); } }} trigger="click" overlayClassName="df-readable-popover" content={<div style={{ maxWidth: 300 }}>{userComps.length === 0 ? (<div style={{ padding: 12, textAlign: 'center', color: '#999', fontSize: 12 }}>暂无已安装组件<br /><a href="/tech-components" target="_blank" style={{ color: '#1677ff' }}>去技术组件页面安装</a></div>) : (userComps.map((c: any) => (<div key={c.id} style={{ padding: '5px 4px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => { const ref = `@组件[${c.name}]#${c.uuid || c.id}`; insertAtCursor(ref); setCompPopOpen(false); }}><Tag color={c.type === 'tool' ? 'green' : 'blue'} style={{ fontSize: 10, margin: 0 }}>{c.type === 'tool' ? '工具' : '开发'}</Tag><span style={{ fontSize: 12, fontWeight: 500 }}>{c.name}</span></div>)))}</div>} title="组件库">
            <Button size="small" className="tc-jm-button tc-right-tool-button" icon={<span>🧩</span>}>组件库</Button>
          </Popover>
          <Button size="small" className="tc-jm-button tc-right-tool-button" icon={<HistoryOutlined />} onClick={onOpenHistory}>引用历史</Button>
        </section>

        <section className="tc-right-prompt-area">
          <TextArea ref={textareaRef as any} value={command} onChange={(e) => setCommand(e.target.value)} onKeyDown={handleKeyDown} onPaste={handlePaste} placeholder={usableFiles.length ? '输入指令，或直接发送让 AI 分析附件...' : '输入任务，或拖拽/粘贴/点击上传文件...'} autoSize={false} disabled={loading} className="tc-jm-textarea tc-right-prompt-textarea" />
        </section>

        <footer className="tc-right-composer-footer">
          <div className="tc-right-attachment-box"><div className={`tc-jm-attachment-list ${attachedFiles.length ? 'has-files' : ''}`}>{attachedFiles.length ? attachedFiles.map(file => (<div key={file.id} className={`tc-jm-attachment-item ${file.error ? 'is-error' : file.uploading ? 'is-uploading' : 'is-ready'}`}>{file.uploading ? <LoadingOutlined /> : file.error ? <ExclamationCircleFilled /> : fileTypeIcon(file.name)}<Text className="tc-jm-attachment-name" ellipsis={{ tooltip: file.error ? `${file.name}: ${file.error}` : file.name }}>{file.name}</Text>{file.uploading ? <span className="tc-jm-attachment-size">{file.progressMsg || '识别中…'}</span> : (!file.error && <span className="tc-jm-attachment-size">{(file.charCount || file.content.length || 0).toLocaleString()}字{file.tokenEstimate ? ` · ~${file.tokenEstimate.toLocaleString()}t` : ''}</span>)}<CloseOutlined className="tc-jm-attachment-remove" onClick={() => removeFile(file.id)} /></div>)) : (<div className="tc-jm-attachment-empty">附件区 · 最多 5 个</div>)}</div></div>
          <div className="tc-right-action-row">
            <input ref={fileInputRef} type="file" multiple accept={ACCEPT_ATTR} style={{ display: 'none' }} onChange={(e) => { if (e.target.files) handleFilesSelect(e.target.files); e.target.value = ''; }} />
            <Tooltip title="上传 Word / Excel / PowerPoint / PDF / 文本 / 图片，最多 5 个"><Button aria-label="上传文件" className="tc-jm-icon-button tc-right-upload-button" icon={<UploadOutlined />} onClick={() => fileInputRef.current?.click()} disabled={loading || attachedFiles.length >= MAX_FILES} /></Tooltip>
            <VoiceInputButton value={command} onChange={setCommand} disabled={loading} size="small" className="tc-right-voice-button" />
            <Tooltip title="发送指令 (Enter)"><Button aria-label="发送指令" className="tc-jm-send-button tc-right-send-button" type="primary" icon={<SendOutlined />} onClick={handleSubmit} loading={loading} disabled={(!command.trim() && !usableFiles.length) || hasPendingUpload} /></Tooltip>
          </div>
        </footer>
      </section>
    </div>
  );
};

export default InputBar;
